import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, set, push, get, query, orderByChild, equalTo } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { usePlans } from '../../hooks/usePlans';
import { apiFetch } from '../../utils/api';
import MobileLayout from '../../components/mobile/MobileLayout';

interface Bot {
  id: string; businessName: string; phoneNumberId: string; accessToken: string;
  welcomeMessage: string; status: string; createdAt: number;
  allowedWebsites?: string[]; industry?: string; template?: string; botType?: string;
}

type BotCategory = 'whatsapp' | 'facebook' | 'website' | 'voice' | 'ai' | null;
type AIChannel = 'whatsapp' | 'website' | 'facebook' | null;
type WhatsAppSetupMode = 'meta_connect' | 'own_number' | 'self_setup' | null;
type FacebookSetupMode = 'oauth' | 'we_setup' | 'self_setup' | null;

const INDUSTRY_TEMPLATES: Record<string, any> = {
  barber: { name: 'Barber Shop', icon: '💈', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome! How can we help?', buttons: [{ id: 'b1', title: 'Book Appointment', payload: 'booking' }, { id: 'b2', title: 'Our Prices', payload: 'prices' }, { id: 'b3', title: 'Location & Hours', payload: 'location' }] }] }, { trigger: 'booking', responses: [{ id: '2', type: 'ask', content: 'What date?', askVariable: 'booking_date', askResponses: [{ id: '3', type: 'ask', content: 'What time?', askVariable: 'booking_time', askResponses: [{ id: '4', type: 'text', content: 'Booked for {{booking_date}} at {{booking_time}}!' }] }] }] }, { trigger: 'prices', responses: [{ id: '5', type: 'text', content: "Men's Cut $10 · Kids $7 · Beard $5" }] }, { trigger: 'location', responses: [{ id: '6', type: 'text', content: '123 Main Street, Harare. Mon-Sat 8am-6pm' }] }] },
  restaurant: { name: 'Restaurant', icon: '🍕', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome!', buttons: [{ id: 'b1', title: 'View Menu', payload: 'menu' }, { id: 'b2', title: 'Book a Table', payload: 'booking' }, { id: 'b3', title: 'Our Hours', payload: 'hours' }] }] }, { trigger: 'menu', responses: [{ id: '2', type: 'text', content: 'Menu at [link]. Breakfast, lunch & dinner daily!' }] }, { trigger: 'booking', responses: [{ id: '3', type: 'ask', content: 'How many people?', askVariable: 'party_size', askResponses: [{ id: '4', type: 'ask', content: 'Date and time?', askVariable: 'booking_time', askResponses: [{ id: '5', type: 'text', content: 'Table for {{party_size}} on {{booking_time}}!' }] }] }] }, { trigger: 'hours', responses: [{ id: '6', type: 'text', content: 'Open daily 7am-10pm.' }] }] },
  retail: { name: 'Clothing Store', icon: '👕', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome to our store!', buttons: [{ id: 'b1', title: 'New Arrivals', payload: 'new' }, { id: 'b2', title: 'Sale Items', payload: 'sale' }, { id: 'b3', title: 'Find a Size', payload: 'sizes' }] }] }, { trigger: 'new', responses: [{ id: '2', type: 'text', content: 'Latest collection at [link]. New stock every Friday!' }] }, { trigger: 'sale', responses: [{ id: '3', type: 'text', content: 'Up to 50% off selected items!' }] }, { trigger: 'sizes', responses: [{ id: '4', type: 'text', content: 'We stock XS-3XL.' }] }] },
  realestate: { name: 'Real Estate', icon: '🏠', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Buy, rent, or sell?', buttons: [{ id: 'b1', title: 'Browse Listings', payload: 'listings' }, { id: 'b2', title: 'Sell My Property', payload: 'sell' }, { id: 'b3', title: 'Book a Viewing', payload: 'viewing' }] }] }, { trigger: 'listings', responses: [{ id: '2', type: 'text', content: 'Browse listings at [link].' }] }, { trigger: 'viewing', responses: [{ id: '3', type: 'ask', content: 'Which property?', askVariable: 'property', askResponses: [{ id: '4', type: 'ask', content: 'Preferred date?', askVariable: 'viewing_date', askResponses: [{ id: '5', type: 'text', content: 'Viewing booked for {{property}} on {{viewing_date}}.' }] }] }] }, { trigger: 'sell', responses: [{ id: '6', type: 'text', content: 'Drop your property details for a free valuation.' }] }] },
  salon: { name: 'Beauty Salon', icon: '💅', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Welcome to our salon!', buttons: [{ id: 'b1', title: 'Book a Service', payload: 'booking' }, { id: 'b2', title: 'Services & Prices', payload: 'prices' }, { id: 'b3', title: 'Contact Us', payload: 'contact' }] }] }, { trigger: 'prices', responses: [{ id: '2', type: 'text', content: 'Hair from $15 · Nails from $10 · Facials from $25.' }] }, { trigger: 'booking', responses: [{ id: '3', type: 'ask', content: 'Which service?', askVariable: 'service', askResponses: [{ id: '4', type: 'ask', content: 'What date?', askVariable: 'date', askResponses: [{ id: '5', type: 'text', content: '{{service}} booked for {{date}}!' }] }] }] }, { trigger: 'contact', responses: [{ id: '6', type: 'text', content: 'Call us on [number] or visit [address].' }] }] },
  custom: { name: 'Start from scratch', icon: '🎨', triggers: [{ trigger: 'hi', responses: [{ id: '1', type: 'buttons', content: 'Hello! How can I help?', buttons: [{ id: 'b1', title: 'Contact Us', payload: 'contact' }, { id: 'b2', title: 'About Us', payload: 'about' }, { id: 'b3', title: 'Get Help', payload: 'help' }] }] }, { trigger: 'contact', responses: [{ id: '2', type: 'text', content: 'Reach us at [email] or [phone].' }] }, { trigger: 'about', responses: [{ id: '3', type: 'text', content: "We're [business name]." }] }, { trigger: 'help', responses: [{ id: '4', type: 'text', content: 'What do you need help with?' }] }] },
};

const industries = [
  { id: 'barber', name: '💈 Barber Shop', description: 'Bookings, prices, location' },
  { id: 'salon', name: '💅 Beauty Salon', description: 'Services, bookings, prices' },
  { id: 'retail', name: '👕 Clothing Store', description: 'Products, sizes, stock' },
  { id: 'restaurant', name: '🍕 Restaurant', description: 'Menu, reservations, hours' },
  { id: 'realestate', name: '🏠 Real Estate', description: 'Listings, viewings, valuations' },
  { id: 'custom', name: '🎨 Start from scratch', description: 'Build your own flows' },
];

const PAID_ONLY_BOT_TYPES = ['whatsapp', 'facebook', 'voice'];
const BUSINESS_PLUS_PLANS = ['business', 'enterprise'];

function getSetupFeeDesc(cat: BotCategory, wa: WhatsAppSetupMode, fb: FacebookSetupMode): string {
  if (cat === 'whatsapp' && wa === 'own_number') return 'WhatsApp Business API – Own Number Registration';
  if (cat === 'facebook' && fb === 'we_setup') return 'Facebook Messenger Bot – Full Setup Service';
  return 'Setup Fee';
}

async function createSetupInvoice(userId: string, businessName: string, botId: string, amount: number, description: string, contactEmail: string): Promise<string> {
  const invRef = push(ref(db, `invoices/${userId}`));
  const now = Date.now();
  const d = new Date(now);
  const dateStr = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  const invoiceNumber = `INV-${dateStr}-${Math.floor(1000 + Math.random() * 9000)}`;
  await set(invRef, { invoiceNumber, botId, businessName, description, amount, status: 'unpaid', type: 'setup_fee', contactEmail, createdAt: now, dueDate: now + 7 * 24 * 60 * 60 * 1000, paidAt: null, transactionId: null, paymentMethod: null, plan: 'setup' });
  return invRef.key!;
}

/* ─── Shared sub-components ─── */
const inp: React.CSSProperties = { width: '100%', padding: '12px 14px', background: 'var(--bg-input)', border: '1px solid var(--card-border)', borderRadius: '9px', color: 'var(--text-primary)', fontSize: '15px', outline: 'none', boxSizing: 'border-box' };
const lbl: React.CSSProperties = { display: 'block', fontWeight: '600', fontSize: '11px', color: 'var(--text-muted)', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '7px' };
const btnPrimary = (disabled = false): React.CSSProperties => ({ width: '100%', padding: '14px', background: disabled ? 'var(--bg-element)' : 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: disabled ? 'var(--text-faint)' : 'white', border: 'none', borderRadius: '11px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '15px' });
const btnGhost: React.CSSProperties = { padding: '14px 20px', background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: '11px', fontWeight: '500', cursor: 'pointer', fontSize: '15px' };

function StepDots({ total, current }: { total: number; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '24px' }}>
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div style={{ width: i + 1 === current ? '24px' : '7px', height: '7px', borderRadius: '4px', background: i + 1 <= current ? '#5B4FE8' : 'var(--bg-element)', opacity: i + 1 <= current ? 1 : 0.4, transition: 'all 0.3s' }} />
          {i < total - 1 && <div style={{ flex: 1, height: '1px', background: i + 1 < current ? '#5B4FE8' : 'var(--bg-element)', opacity: 0.4 }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function BotTypeCard({ icon, label, description, badge, badgeColor = '#5B4FE8', selected, locked, lockReason, onClick }: { icon: string; label: string; description: string; badge?: string; badgeColor?: string; selected: boolean; locked?: boolean; lockReason?: string; onClick: () => void }) {
  return (
    <div onClick={locked ? undefined : onClick} style={{ padding: '16px 12px', borderRadius: '14px', cursor: locked ? 'not-allowed' : 'pointer', position: 'relative', border: selected ? '2px solid #5B4FE8' : '1px solid var(--card-border)', background: selected ? 'rgba(91,79,232,0.10)' : locked ? 'var(--bg-page)' : 'var(--bg-card)', textAlign: 'center', opacity: locked ? 0.55 : 1 }}>
      {locked && <div style={{ position: 'absolute', top: '8px', right: '8px', background: '#f59e0b22', color: '#f59e0b', fontSize: '8px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px' }}>🔒</div>}
      {badge && !locked && <div style={{ position: 'absolute', top: '8px', right: '8px', background: badgeColor + '22', color: badgeColor, fontSize: '8px', fontWeight: '700', padding: '2px 5px', borderRadius: '20px' }}>{badge}</div>}
      {selected && !locked && <div style={{ position: 'absolute', top: '8px', left: '8px', width: '16px', height: '16px', borderRadius: '50%', background: '#5B4FE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '9px' }}>✓</span></div>}
      <div style={{ fontSize: '32px', marginBottom: '8px', lineHeight: 1 }}>{icon}</div>
      <div style={{ color: locked ? 'var(--text-faint)' : 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: 'var(--text-faint)', fontSize: '11px', lineHeight: '1.4' }}>{description}</div>
      {locked && lockReason && <div style={{ marginTop: '8px', fontSize: '10px', color: '#f59e0b' }}>{lockReason}</div>}
    </div>
  );
}

function SetupOptionCard({ icon, title, description, fee, feeColor = '#10b981', badge, badgeColor = '#5B4FE8', selected, locked, lockReason, onClick }: { icon: string; title: string; description: string; fee?: string; feeColor?: string; badge?: string; badgeColor?: string; selected: boolean; locked?: boolean; lockReason?: string; onClick: () => void }) {
  return (
    <div onClick={locked ? undefined : onClick} style={{ padding: '16px', borderRadius: '13px', cursor: locked ? 'not-allowed' : 'pointer', position: 'relative', border: selected ? '2px solid #5B4FE8' : '1px solid var(--card-border)', background: selected ? 'rgba(91,79,232,0.08)' : locked ? 'var(--bg-page)' : 'var(--bg-card)', minHeight: '44px', opacity: locked ? 0.55 : 1 }}>
      {locked && <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#f59e0b22', color: '#f59e0b', fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px' }}>🔒 PAID</div>}
      {badge && !locked && <div style={{ position: 'absolute', top: '10px', right: '10px', background: badgeColor + '22', color: badgeColor, fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '20px' }}>{badge}</div>}
      {selected && !locked && <div style={{ position: 'absolute', top: '10px', left: '10px', width: '16px', height: '16px', borderRadius: '50%', background: '#5B4FE8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ color: 'white', fontSize: '9px' }}>✓</span></div>}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', paddingLeft: selected ? '24px' : '0' }}>
        <span style={{ fontSize: '24px', flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: locked ? 'var(--text-faint)' : 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>{title}</div>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', lineHeight: '1.5' }}>{description}</div>
          {locked && lockReason ? (
            <div style={{ display: 'inline-block', marginTop: '8px', background: '#f59e0b18', color: '#f59e0b', fontSize: '11px', fontWeight: '700', padding: '2px 9px', borderRadius: '20px' }}>{lockReason}</div>
          ) : fee && (
            <div style={{ display: 'inline-block', marginTop: '8px', background: feeColor + '18', color: feeColor, fontSize: '11px', fontWeight: '700', padding: '2px 9px', borderRadius: '20px' }}>{fee}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoBox({ items, color = '#5B4FE8' }: { items: string[]; color?: string }) {
  return (
    <div style={{ background: color + '0d', border: `1px solid ${color}30`, borderLeft: `3px solid ${color}`, borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: i < items.length - 1 ? '6px' : 0 }}>
          <span style={{ color, fontSize: '10px', marginTop: '5px', flexShrink: 0 }}>●</span>
          <span style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.5' }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

export default function CreateBotMobile() {
  const navigate = useNavigate();
  const { getBotLimit, getSetupFee } = usePlans();
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [bots, setBots] = useState<Bot[]>([]);
  const [step, setStep] = useState(1);

  const [botCategory, setBotCategory] = useState<BotCategory>(null);
  const [aiChannel, setAIChannel] = useState<AIChannel>(null);
  const [datasets, setDatasets] = useState<Array<{ id: string; name: string; format: string }>>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [whatsappMode, setWhatsappMode] = useState<WhatsAppSetupMode>(null);
  const [facebookMode, setFacebookMode] = useState<FacebookSetupMode>(null);
  const [fbReplyMode, setFbReplyMode] = useState<'messages' | 'comments'>('messages');
  const [fbOAuthStatus, setFbOAuthStatus] = useState<'idle' | 'loading' | 'pages' | 'done'>('idle');
  const [fbOAuthPages, setFbOAuthPages] = useState<Array<{ id: string; name: string; access_token: string; category: string }>>([]);
  const [waOAuthStatus, setWaOAuthStatus] = useState<'idle' | 'loading' | 'numbers' | 'done'>('idle');
  const [waNumbers, setWaNumbers] = useState<Array<{ id: string; display_phone_number: string; verified_name: string }>>([]);

  const [businessName, setBusinessName] = useState('');
  const [industry, setIndustry] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [ownPhoneNumber, setOwnPhoneNumber] = useState('');
  const [ownPhoneCountry, setOwnPhoneCountry] = useState('ZW');
  const [selfPhoneNumberId, setSelfPhoneNumberId] = useState('');
  const [selfAccessToken, setSelfAccessToken] = useState('');
  const [fbPageId, setFbPageId] = useState('');
  const [fbPageToken, setFbPageToken] = useState('');
  const [fbPageUrl, setFbPageUrl] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [firebaseTemplates, setFirebaseTemplates] = useState<any[]>([]);

  const loadFirebaseTemplates = async (botType: string) => {
    try {
      const snap = await get(ref(db, 'templates'));
      if (!snap.exists()) return;
      const list: any[] = [];
      snap.forEach(c => {
        const t = { id: c.key, ...c.val() };
        if (t.botType === botType) {
          const isOwn = auth.currentUser && t.createdBy === auth.currentUser.uid;
          if (t.isPublic !== false || isOwn) list.push(t);
        }
      });
      setFirebaseTemplates(list);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (cu) => {
      if (cu) {
        const snap = await get(ref(db, `users/${cu.uid}`));
        setUserData(snap.val());
        const bQ = query(ref(db, 'bots'), orderByChild('userId'), equalTo(cu.uid));
        const bS = await get(bQ);
        const list: Bot[] = [];
        bS.forEach(c => { list.push({ id: c.key!, ...c.val() }); });
        setBots(list);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (botCategory !== 'facebook' && botCategory !== 'whatsapp') return;
    if ((window as any).FB) return;
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.onload = () => {
      (window as any).FB.init({ appId: process.env.REACT_APP_FACEBOOK_APP_ID || '', version: 'v21.0', xfbml: false, cookie: true });
    };
    document.head.appendChild(script);
  }, [botCategory]);

  const handleFacebookOAuth = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Facebook SDK not loaded. Please refresh and try again.'); return; }
    setFbOAuthStatus('loading');
    FB.login((response: any) => {
      if (!response.authResponse) { setFbOAuthStatus('idle'); return; }
      apiFetch('/api/bots/facebook-page-exchange', { method: 'POST', body: JSON.stringify({ accessToken: response.authResponse.accessToken }) })
        .then(r => r.json())
        .then(data => {
          if (!data.success) {
            setFbOAuthStatus('idle');
            const msg = data.friendly || data.error || 'Meta connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          setFbOAuthPages(data.pages);
          setFbOAuthStatus('pages');
        })
        .catch(() => { setFbOAuthStatus('idle'); alert('Connection failed. Please check your internet and try again.'); });
    }, { scope: fbReplyMode === 'comments'
      ? 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_engagement,pages_manage_posts'
      : 'pages_show_list,pages_messaging,pages_manage_metadata,pages_manage_posts' });
  };

  const handleSelectFBPage = (page: { id: string; name: string; access_token: string }) => {
    setFbPageId(page.id); setFbPageToken(page.access_token); setFbOAuthStatus('done');
  };

  // Meta Embedded Signup postMessage listener — captures WABA/phone data mid-wizard
  useEffect(() => {
    if (botCategory !== 'whatsapp') return;
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'CANCEL') {
          console.log('[Embedded Signup] User cancelled at step:', data.data?.current_step);
        } else if (data.event === 'ERROR') {
          console.error('[Embedded Signup] Error:', data.data?.error_message);
        }
      } catch (_) {}
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [botCategory]);

  const handleMetaConnect = () => {
    const FB = (window as any).FB;
    if (!FB) { alert('Meta SDK not loaded. Please refresh and try again.'); return; }
    setWaOAuthStatus('loading');

    const configId = process.env.REACT_APP_META_CONFIG_ID;
    const loginOptions: any = configId
      ? { config_id: configId, response_type: 'code', override_default_response_type: true, extras: { feature: 'whatsapp_embedded_signup' } }
      : { scope: 'business_management,whatsapp_business_management' };

    FB.login((response: any) => {
      if (!response.authResponse) { setWaOAuthStatus('idle'); return; }

      const body: any = configId
        ? { code: response.authResponse.code }
        : { accessToken: response.authResponse.accessToken };

      apiFetch('/api/bots/meta-exchange', { method: 'POST', body: JSON.stringify(body) })
        .then(r => r.json())
        .then(data => {
          if (!data.success) {
            setWaOAuthStatus('idle');
            const msg = data.friendly || data.error || 'Meta connection failed. Please try again.';
            alert(data.action && data.actionUrl ? `${msg}\n\n${data.action}: ${data.actionUrl}` : msg);
            return;
          }
          setSelfAccessToken(data.longLivedToken);
          setWaNumbers(data.phoneNumbers || []);
          setWaOAuthStatus(data.phoneNumbers?.length ? 'numbers' : 'done');
        })
        .catch(() => {
          setWaOAuthStatus('idle');
          alert('Connection failed. Please check your internet and try again.');
        });
    }, loginOptions);
  };

  const handleSelectWANumber = (num: { id: string; display_phone_number: string; verified_name: string }) => {
    setSelfPhoneNumberId(num.id); setWaOAuthStatus('done');
  };

  const userPlan: string = userData?.plan || 'free';
  const isFreePlan = userPlan === 'free';
  const isBusinessOrAbove = BUSINESS_PLUS_PLANS.includes(userPlan);
  const canOwnWhatsAppNumber = ['business', 'agency', 'enterprise'].includes(userPlan);
  const botLimit = getBotLimit(userPlan);
  const atBotLimit = bots.length >= botLimit;
  const isBotTypeLocked = (t: BotCategory) => t === 'ai' ? !isBusinessOrAbove : isFreePlan && PAID_ONLY_BOT_TYPES.includes(t as string);
  const isAIFlow = botCategory === 'ai';

  const loadDatasets = async () => {
    if (!auth.currentUser) return;
    setDatasetsLoading(true);
    try {
      const snap = await get(ref(db, `datasets/${auth.currentUser.uid}`));
      const list: Array<{ id: string; name: string; format: string }> = [];
      if (snap.exists()) snap.forEach(c => { list.push({ id: c.key!, name: c.val().name, format: c.val().format }); });
      setDatasets(list);
    } catch (e) { console.error(e); }
    finally { setDatasetsLoading(false); }
  };

  useEffect(() => { if (isAIFlow) loadDatasets(); }, [isAIFlow]);
  const needsSetupModeStep = !isAIFlow && (botCategory === 'whatsapp' || botCategory === 'facebook');
  const activeMode = botCategory === 'whatsapp' ? whatsappMode : botCategory === 'facebook' ? facebookMode : '';
  const setupFee = isAIFlow ? 0 : getSetupFee(botCategory || '', activeMode || '');
  const setupFeeDesc = getSetupFeeDesc(botCategory, whatsappMode, facebookMode);

  const canProceedStep1 = botCategory !== null && !isBotTypeLocked(botCategory);
  const canProceedStep2 = (() => {
    if (isAIFlow) return aiChannel !== null;
    if (botCategory === 'whatsapp') { if (!whatsappMode) return false; if (whatsappMode === 'meta_connect') return waOAuthStatus === 'done'; return true; }
    if (botCategory === 'facebook') { if (!facebookMode) return false; if (facebookMode === 'oauth') return fbOAuthStatus === 'done'; return true; }
    return true;
  })();
  const canProceedStep3 = (() => {
    if (isAIFlow) {
      if (!businessName.trim()) return false;
      if (aiChannel === 'whatsapp') return selfPhoneNumberId.trim().length > 0 && selfAccessToken.trim().length > 0;
      if (aiChannel === 'facebook') return fbPageId.trim().length > 0 && fbPageToken.trim().length > 0;
      if (aiChannel === 'website') return contactEmail.trim().length > 0;
      return false;
    }
    if (!businessName.trim() || !industry) return false;
    if (botCategory === 'whatsapp') {
      if (whatsappMode === 'meta_connect') return true;
      if (whatsappMode === 'own_number') return ownPhoneNumber.trim().length > 0 && contactEmail.trim().length > 0;
      if (whatsappMode === 'self_setup') return selfPhoneNumberId.trim().length > 0 && selfAccessToken.trim().length > 0;
    }
    if (botCategory === 'facebook') {
      if (facebookMode === 'oauth') return true;
      if (facebookMode === 'we_setup') return contactEmail.trim().length > 0;
      if (facebookMode === 'self_setup') return fbPageId.trim().length > 0 && fbPageToken.trim().length > 0;
    }
    if (botCategory === 'website' || botCategory === 'voice') return websiteUrl.trim().length > 0 && contactEmail.trim().length > 0;
    return false;
  })();

  const goNext = (s: number) => {
    if (s === 1) { (isAIFlow || needsSetupModeStep) ? setStep(2) : setStep(3); return; }
    if (s === 2) { setStep(3); return; }
    if (s === 3) { if (isAIFlow) { createBot(); return; } setStep(4); loadFirebaseTemplates(botCategory || 'whatsapp'); return; }
  };
  const goBack = (s: number) => {
    if (s === 2) { setStep(1); return; }
    if (s === 3) { (isAIFlow || needsSetupModeStep) ? setStep(2) : setStep(1); return; }
    if (s === 4) { setStep(3); return; }
  };

  const totalDots = isAIFlow ? 3 : (needsSetupModeStep ? 4 : 3);
  const visualStep = isAIFlow ? step : (needsSetupModeStep ? step : step === 3 ? 2 : step === 4 ? 3 : 1);

  // A Facebook Page / WhatsApp number connected to two bots at once means
  // Meta's webhook can only ever deliver to one of them - the other bot's
  // owner would see zero incoming messages with no indication why. Block
  // that at creation time instead of letting it fail silently later.
  const findConnectionConflict = async (field: 'fbPageId' | 'phoneNumberId', value: string, fbMode?: 'messenger' | 'comments'): Promise<{ id: string; businessName?: string } | null> => {
    if (!value) return null;
    const q = query(ref(db, 'bots'), orderByChild(field), equalTo(value));
    const snap = await get(q);
    const val = snap.val();
    if (!val) return null;
    for (const [id, b] of Object.entries<any>(val)) {
      if (b?.status === 'deleted') continue;
      if (fbMode && (b?.fbMode === 'comments' ? 'comments' : 'messenger') !== fbMode) continue;
      return { id, businessName: b?.businessName };
    }
    return null;
  };

  const createBot = async () => {
    if (!auth.currentUser) { alert('Please log in first'); return; }
    if (atBotLimit) { navigate('/billing'); return; }
    if (botCategory && isBotTypeLocked(botCategory)) { navigate('/billing'); return; }
    if (!businessName.trim()) { alert('Please enter a business name'); return; }
    setLoading(true);
    try {
      const uid = auth.currentUser.uid;
      const botRef = push(ref(db, 'bots'));
      const botId = botRef.key!;
      const fbTpl = firebaseTemplates.find(t => t.id === selectedTemplate);
      let triggers: any[];
      if (fbTpl) {
        triggers = (fbTpl.triggers || []).map((t: any) => { const c = JSON.parse(JSON.stringify(t)); if (c.trigger === 'hi' && c.responses[0]?.content) c.responses[0].content = c.responses[0].content.replace(/Greenfield Academy/g, businessName); return c; });
      } else {
        const tpl = INDUSTRY_TEMPLATES[selectedTemplate] || INDUSTRY_TEMPLATES.custom;
        triggers = tpl.triggers.map((t: any) => { const c = JSON.parse(JSON.stringify(t)); if (c.trigger === 'hi' && c.responses[0]?.content) c.responses[0].content = `Welcome to ${businessName}! ` + c.responses[0].content; return c; });
      }
      // accessToken is a real credential - written separately to
      // botSecrets/{id} (owner-scoped), never embedded in bots/{id} (broadly
      // readable by any logged-in user for the dashboard list query).
      let pendingAccessToken: string | null = null;
      if (isAIFlow) {
        const botData: any = { businessName: businessName.trim(), userId: uid, welcomeMessage: welcomeMessage || `Hi! I'm ${businessName}'s AI assistant.`, createdAt: Date.now(), status: 'active', botType: 'ai', aiChannel, contactEmail: contactEmail.trim(), aiConfig: { model: 'openai/gpt-oss-20b', ...(selectedDatasetId ? { datasetId: selectedDatasetId } : {}) }, setupFeeAmount: 0, setupFeePaid: true };
        if (aiChannel === 'whatsapp') { botData.phoneNumberId = selfPhoneNumberId.trim(); pendingAccessToken = selfAccessToken.trim(); }
        else if (aiChannel === 'facebook') { botData.fbPageId = fbPageId.trim(); pendingAccessToken = fbPageToken.trim(); }
        else if (aiChannel === 'website') { botData.websiteUrl = websiteUrl.trim(); }
        if (aiChannel === 'whatsapp' && botData.phoneNumberId) {
          const conflict = await findConnectionConflict('phoneNumberId', botData.phoneNumberId);
          if (conflict) { alert(`This WhatsApp number is already connected to another bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's number first, or use a different number.`); return; }
        } else if (aiChannel === 'facebook' && botData.fbPageId) {
          const conflict = await findConnectionConflict('fbPageId', botData.fbPageId, 'messenger');
          if (conflict) { alert(`This Facebook Page is already connected to another messaging bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's Page first, or connect a different Page.`); return; }
        }
        await set(botRef, botData);
        if (pendingAccessToken) await set(ref(db, `botSecrets/${botId}`), { accessToken: pendingAccessToken });
        apiFetch('/api/notify/bot-created', { method: 'POST', body: JSON.stringify({ botId, botName: businessName.trim(), botType: 'ai', aiChannel, setupFee: 0 }) }).catch(() => {});
        navigate('/dashboard'); return;
      }
      const botData: any = { businessName: businessName.trim(), userId: uid, welcomeMessage: welcomeMessage || `Welcome to ${businessName}! How can I help you today?`, createdAt: Date.now(), status: 'active', industry: industry || selectedTemplate, template: selectedTemplate, botType: botCategory, contactEmail: contactEmail.trim(), setupFeeAmount: setupFee, setupFeePaid: setupFee === 0 };
      if (botCategory === 'whatsapp') {
        botData.whatsappMode = whatsappMode;
        if (whatsappMode === 'meta_connect') { botData.phoneNumberId = selfPhoneNumberId.trim(); pendingAccessToken = selfAccessToken.trim(); botData.status = 'active'; }
        else if (whatsappMode === 'own_number') { botData.phoneNumber = ownPhoneNumber.trim(); botData.country = ownPhoneCountry; botData.businessAddress = businessAddress.trim(); botData.websiteUrl = websiteUrl.trim(); botData.status = 'pending_setup'; }
        else if (whatsappMode === 'self_setup') { botData.phoneNumberId = selfPhoneNumberId.trim(); pendingAccessToken = selfAccessToken.trim(); botData.status = 'active'; }
      } else if (botCategory === 'facebook') {
        botData.facebookMode = facebookMode;
        botData.fbMode = fbReplyMode === 'comments' ? 'comments' : 'messenger';
        if (facebookMode === 'oauth' || facebookMode === 'self_setup') { botData.fbPageId = fbPageId.trim(); pendingAccessToken = fbPageToken.trim(); botData.status = 'active'; }
        else if (facebookMode === 'we_setup') { botData.fbPageUrl = fbPageUrl.trim(); botData.status = 'pending_setup'; }
      } else if (botCategory === 'website') { botData.websiteUrl = websiteUrl.trim(); }
      else if (botCategory === 'voice') { botData.websiteUrl = websiteUrl.trim(); botData.status = 'pending_setup'; }
      if (botCategory === 'whatsapp' && botData.phoneNumberId) {
        const conflict = await findConnectionConflict('phoneNumberId', botData.phoneNumberId);
        if (conflict) { alert(`This WhatsApp number is already connected to another bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. Messages would only ever reach one of them — disconnect that bot's number first, or use a different number.`); return; }
      } else if (botCategory === 'facebook' && botData.fbPageId) {
        const conflict = await findConnectionConflict('fbPageId', botData.fbPageId, botData.fbMode);
        if (conflict) {
          const kind = botData.fbMode === 'comments' ? 'comment-reply' : 'messaging';
          alert(`This Facebook Page is already connected to another ${kind} bot${conflict.businessName ? ` ("${conflict.businessName}")` : ''}. ${botData.fbMode === 'comments' ? 'Comments' : 'Messages'} would only ever reach one of them — disconnect that bot's Page first, or connect a different Page.`);
          return;
        }
      }
      await set(botRef, botData);
      if (pendingAccessToken) await set(ref(db, `botSecrets/${botId}`), { accessToken: pendingAccessToken });
      await set(ref(db, `flows/${botId}`), { name: businessName, botId, triggers, userId: uid, createdAt: Date.now(), updatedAt: Date.now() });
      if ((botCategory === 'whatsapp' && whatsappMode === 'self_setup') || (botCategory === 'facebook' && facebookMode === 'self_setup')) {
        apiFetch(`/api/bots/${botId}/self-setup-subscribe`, { method: 'POST' })
          .then(r => r.json())
          .then(d => { if (!d.success) console.warn('Webhook subscription failed:', d.friendly || d.error); })
          .catch(() => {});
      }
      if (setupFee > 0) {
        const invoiceId = await createSetupInvoice(uid, businessName.trim(), botId, setupFee, setupFeeDesc, contactEmail.trim());
        await set(ref(db, `bots/${botId}/pendingInvoiceId`), invoiceId);
        await set(ref(db, `bots/${botId}/status`), 'pending_payment');
        apiFetch('/api/notify/bot-created', { method: 'POST', body: JSON.stringify({ botId, botName: businessName.trim(), botType: botCategory, setupFee, invoiceId }) }).catch(() => {});
        navigate(`/billing?invoiceId=${invoiceId}`); return;
      }
      apiFetch('/api/notify/bot-created', { method: 'POST', body: JSON.stringify({ botId, botName: businessName.trim(), botType: botCategory, setupFee: 0 }) }).catch(() => {});
      navigate('/dashboard');
    } catch (e) { console.error(e); alert('Error creating bot: ' + e); }
    finally { setLoading(false); }
  };

  const stepTitles = ['Choose channel', 'Setup method', 'Your details', 'Pick template'];
  const currentTitle = stepTitles[(isAIFlow ? [0, 1, 2] : needsSetupModeStep ? [0, 1, 2, 3] : [0, 2, 3])[step - 1]] || 'Create Bot';

  return (
    <MobileLayout title="New Bot" onBack={step > 1 ? () => goBack(step) : undefined}>
      <div style={{ padding: '20px 16px 32px' }}>

        {/* Step indicator */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ color: '#5B4FE8', fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Step {visualStep} of {totalDots}</div>
          <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>{currentTitle}</div>
          <StepDots total={totalDots} current={visualStep} />
        </div>

        {/* Bot limit warning */}
        {atBotLimit && (
          <div style={{ background: '#f59e0b0d', border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '10px', padding: '14px', marginBottom: '20px' }}>
            <div style={{ color: '#f59e0b', fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>Bot limit reached</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>Your <strong>{userPlan}</strong> plan allows {botLimit} bot{botLimit > 1 ? 's' : ''}.</div>
            <button onClick={() => navigate('/billing')} style={{ ...btnPrimary(false), background: '#f59e0b', width: 'auto', padding: '8px 18px', fontSize: '13px' }}>Upgrade plan</button>
          </div>
        )}

        {/* ── STEP 1: Channel ── */}
        {step === 1 && (
          <div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>Pick the channel your customers use most</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
              <BotTypeCard icon="💬" label="WhatsApp" badge="POPULAR" badgeColor="#10b981" description="Direct WhatsApp chat for bookings, FAQs & sales" selected={botCategory === 'whatsapp'} locked={isBotTypeLocked('whatsapp')} lockReason="Paid plan" onClick={() => { setBotCategory('whatsapp'); setWhatsappMode(null); }} />
              <BotTypeCard icon="📘" label="Facebook" description="Auto-reply on your Facebook business page" selected={botCategory === 'facebook'} locked={isBotTypeLocked('facebook')} lockReason="Paid plan" onClick={() => { setBotCategory('facebook'); setFacebookMode(null); }} />
              <BotTypeCard icon="🌐" label="Website Chat" badge="FREE" badgeColor="#10b981" description="Chat bubble on your website" selected={botCategory === 'website'} onClick={() => setBotCategory('website')} />
              <BotTypeCard icon="🎙️" label="Voice Bot" badge="BETA" badgeColor="#f59e0b" description="AI voice assistant on your website" selected={botCategory === 'voice'} locked={isBotTypeLocked('voice')} lockReason="Paid plan" onClick={() => setBotCategory('voice')} />
              <div style={{ gridColumn: '1 / -1' }}>
                <BotTypeCard icon="🤖" label="AI Bot" badge="BUSINESS+" badgeColor="#a855f7" description="Powered by AI — answers everything automatically on WhatsApp, Website, or Facebook" selected={botCategory === 'ai'} locked={isBotTypeLocked('ai')} lockReason="Business plan required" onClick={() => { setBotCategory('ai'); setAIChannel(null); }} />
              </div>
            </div>
            {botCategory === 'facebook' && (
              <div style={{ marginBottom: '16px' }}>
                <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '8px' }}>What should it do on your Page?</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <SetupOptionCard icon="💬" title="Messages" description="Auto-reply to private messages people send your Page." selected={fbReplyMode === 'messages'} onClick={() => setFbReplyMode('messages')} />
                  <SetupOptionCard icon="📝" title="Comments" description="Auto-reply to comments on your Page's posts, publicly, via AI." badge="NEW" badgeColor="#a855f7" selected={fbReplyMode === 'comments'} onClick={() => setFbReplyMode('comments')} />
                </div>
              </div>
            )}
            {isFreePlan && (
              <div style={{ background: '#5B4FE80d', border: '1px solid #5B4FE830', borderRadius: '10px', padding: '12px 14px', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>🔒 WhatsApp, Facebook & Voice require a paid plan.</span>
                <button onClick={() => navigate('/billing')} style={{ padding: '6px 14px', background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '12px', cursor: 'pointer', flexShrink: 0 }}>Upgrade</button>
              </div>
            )}
            <button onClick={() => atBotLimit ? navigate('/billing') : goNext(1)} disabled={!canProceedStep1 || atBotLimit} style={btnPrimary(!canProceedStep1 || atBotLimit)}>
              {atBotLimit ? 'Upgrade to continue' : 'Continue →'}
            </button>
          </div>
        )}

        {/* ── STEP 2: AI channel picker ── */}
        {step === 2 && isAIFlow && (
          <div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>Where should your AI bot live?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <SetupOptionCard icon="💬" title="WhatsApp" description="AI replies to every WhatsApp message. Needs your WhatsApp Business API credentials." badge="POPULAR" badgeColor="#10b981" selected={aiChannel === 'whatsapp'} onClick={() => setAIChannel('whatsapp')} />
              <SetupOptionCard icon="🌐" title="Website Chat" description="Smart AI chat widget on your website. No extra credentials needed." selected={aiChannel === 'website'} onClick={() => setAIChannel('website')} />
              <SetupOptionCard icon="📘" title="Facebook Messenger" description="AI auto-responds to every Messenger message on your Page." selected={aiChannel === 'facebook'} onClick={() => setAIChannel('facebook')} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => goBack(2)} style={btnGhost}>← Back</button>
              <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={{ ...btnPrimary(!canProceedStep2), flex: 1 }}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: WhatsApp setup mode ── */}
        {step === 2 && botCategory === 'whatsapp' && (
          <div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>How would you like to set up your WhatsApp?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <SetupOptionCard icon="🔗" title="Connect Meta Business" description="Sign in with Meta and we auto-detect your WhatsApp number. Fastest path to live." badge={canOwnWhatsAppNumber ? "RECOMMENDED" : undefined} badgeColor="#10b981" selected={whatsappMode === 'meta_connect'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+ — use desktop for the free shared number" onClick={() => { if (!canOwnWhatsAppNumber) return; setWhatsappMode('meta_connect'); setWaOAuthStatus('idle'); }} />
              <SetupOptionCard icon="📱" title="We register your number" description="Provide your number and we handle Meta registration. Takes 1–3 business days." fee="$20 one-time" feeColor="#f59e0b" selected={whatsappMode === 'own_number'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+ — use desktop for the free shared number" onClick={() => canOwnWhatsAppNumber && setWhatsappMode('own_number')} />
              <SetupOptionCard icon="⚙️" title="I have API access" description="Paste your Phone Number ID and Access Token from Meta Developer Console." badge="ADVANCED" badgeColor="var(--text-muted)" selected={whatsappMode === 'self_setup'} locked={!canOwnWhatsAppNumber} lockReason="Requires Business plan+ — use desktop for the free shared number" onClick={() => canOwnWhatsAppNumber && setWhatsappMode('self_setup')} />
              {!canOwnWhatsAppNumber && (
                <div style={{ background: '#5B4FE812', border: '1px solid #5B4FE830', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  🧪 Your plan uses our free shared WhatsApp number — that option isn't available on mobile yet. Please open Autopilot on a desktop browser to set it up.
                </div>
              )}
            </div>
            {whatsappMode === 'meta_connect' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                {waOAuthStatus === 'idle' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '14px' }}>Connect your Meta Business account to auto-detect your WhatsApp number.</div>
                    <button onClick={handleMetaConnect} style={{ background: 'linear-gradient(135deg,#0082fb,#0064d9)', color: '#fff', border: 'none', borderRadius: '9px', padding: '12px 22px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      🔗 Connect Meta Business
                    </button>
                  </div>
                )}
                {waOAuthStatus === 'loading' && <div style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Connecting to Meta…</div>}
                {waOAuthStatus === 'numbers' && (
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>Select your WhatsApp number:</div>
                    {waNumbers.map(n => (
                      <div key={n.id} onClick={() => handleSelectWANumber(n)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '9px', cursor: 'pointer', border: '1px solid var(--card-border)', background: 'var(--bg-page)', marginBottom: '8px', minHeight: '44px' }}>
                        <span style={{ fontSize: '20px' }}>📱</span>
                        <div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>{n.display_phone_number}</div>
                          <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{n.verified_name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {waOAuthStatus === 'done' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>Number connected!</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{waNumbers.find(n => n.id === selfPhoneNumberId)?.display_phone_number || selfPhoneNumberId}</div>
                    </div>
                    <button onClick={() => { setWaOAuthStatus('idle'); setSelfPhoneNumberId(''); setSelfAccessToken(''); }} style={{ background: 'transparent', border: '1px solid var(--card-border)', color: 'var(--text-muted)', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}>Change</button>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => goBack(2)} style={btnGhost}>← Back</button>
              <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={{ ...btnPrimary(!canProceedStep2), flex: 1 }}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Facebook setup mode ── */}
        {step === 2 && botCategory === 'facebook' && (
          <div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>How would you like to set up your Facebook bot?</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <SetupOptionCard icon="📘" title="Connect via Facebook" description="Sign in with Facebook and we link your Page automatically. Go live instantly." badge="RECOMMENDED" badgeColor="#10b981" selected={facebookMode === 'oauth'} onClick={() => { setFacebookMode('oauth'); setFbOAuthStatus('idle'); }} />
              <SetupOptionCard icon="🛠️" title="We set it up for you" description="Our team connects your Page. Just give us your Page name or URL. 1–2 business days." fee="$50 one-time" feeColor="#f59e0b" selected={facebookMode === 'we_setup'} onClick={() => setFacebookMode('we_setup')} />
              <SetupOptionCard icon="⚙️" title="I have credentials" description="Paste your Page ID and Page Access Token from Meta Developer Console." badge="ADVANCED" badgeColor="var(--text-muted)" selected={facebookMode === 'self_setup'} onClick={() => setFacebookMode('self_setup')} />
            </div>
            {facebookMode === 'oauth' && (
              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                {fbOAuthStatus === 'idle' && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px', marginBottom: '14px' }}>Sign in with Facebook to select your Page.</div>
                    <button onClick={handleFacebookOAuth} style={{ background: '#1877F2', color: '#fff', border: 'none', borderRadius: '9px', padding: '12px 22px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>📘 Continue with Facebook</button>
                  </div>
                )}
                {fbOAuthStatus === 'loading' && <div style={{ color: 'var(--text-muted)', textAlign: 'center' }}>Connecting…</div>}
                {fbOAuthStatus === 'pages' && (
                  <div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '10px' }}>Select your Page:</div>
                    {fbOAuthPages.map(p => (
                      <div key={p.id} onClick={() => handleSelectFBPage(p)} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '9px', cursor: 'pointer', border: '1px solid var(--card-border)', background: 'var(--bg-page)', marginBottom: '8px', minHeight: '44px' }}>
                        <span style={{ fontSize: '20px' }}>📄</span>
                        <div>
                          <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px' }}>{p.name}</div>
                          <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{p.category}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {fbOAuthStatus === 'done' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#10b98120', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>Page connected!</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{fbOAuthPages.find(p => p.id === fbPageId)?.name || fbPageId}</div>
                    </div>
                    <button onClick={() => { setFbOAuthStatus('idle'); setFbPageId(''); setFbPageToken(''); }} style={{ background: 'transparent', border: '1px solid var(--card-border)', color: 'var(--text-muted)', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer' }}>Change</button>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => goBack(2)} style={btnGhost}>← Back</button>
              <button onClick={() => goNext(2)} disabled={!canProceedStep2} style={{ ...btnPrimary(!canProceedStep2), flex: 1 }}>Continue →</button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Business details ── */}
        {step === 3 && (
          <div>
            {setupFee > 0 && (
              <div style={{ background: '#f59e0b0d', border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '10px', padding: '14px', marginBottom: '16px', display: 'flex', gap: '12px' }}>
                <span style={{ fontSize: '20px', flexShrink: 0 }}>🧾</span>
                <div>
                  <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '14px', marginBottom: '3px' }}>One-time setup fee: ${setupFee}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '1.5' }}>An invoice will be created. You'll pay from Billing before the bot activates.</div>
                </div>
              </div>
            )}

            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={lbl}>Business Name <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="e.g. Acme Hair Salon" style={inp} />
              </div>
              {!isAIFlow && (
                <div>
                  <label style={lbl}>Industry <span style={{ color: '#ef4444' }}>*</span></label>
                  <select value={industry} onChange={e => setIndustry(e.target.value)} style={{ ...inp, appearance: 'none' as const }}>
                    <option value="">Select your industry</option>
                    {industries.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label style={lbl}>Welcome Message</label>
                <textarea value={welcomeMessage} onChange={e => setWelcomeMessage(e.target.value)} rows={3} placeholder={`Welcome to ${businessName || 'our business'}! How can I help?`} style={{ ...inp, resize: 'vertical' as const }} />
              </div>

              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />

              {/* WhatsApp own_number */}
              {botCategory === 'whatsapp' && whatsappMode === 'own_number' && (<>
                <InfoBox items={['We submit your details to Meta for WhatsApp Business API access', 'Your number must NOT already be on personal WhatsApp', 'Approval takes 1–3 business days']} color="#10b981" />
                <div>
                  <label style={lbl}>Country</label>
                  <select value={ownPhoneCountry} onChange={e => setOwnPhoneCountry(e.target.value)} style={{ ...inp, appearance: 'none' as const }}>
                    <option value="ZW">🇿🇼 +263</option>
                    <option value="ZA">🇿🇦 +27</option>
                    <option value="NG">🇳🇬 +234</option>
                    <option value="KE">🇰🇪 +254</option>
                    <option value="US">🇺🇸 +1</option>
                    <option value="GB">🇬🇧 +44</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Phone Number <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="tel" value={ownPhoneNumber} onChange={e => setOwnPhoneNumber(e.target.value)} placeholder="77 123 4567" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Business Address</label>
                  <input type="text" value={businessAddress} onChange={e => setBusinessAddress(e.target.value)} placeholder="123 Main St, Harare" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Business Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Website (optional)</label>
                  <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yourbusiness.com" style={inp} />
                </div>
              </>)}

              {/* WhatsApp self_setup */}
              {botCategory === 'whatsapp' && whatsappMode === 'self_setup' && (<>
                <InfoBox items={['Find your Phone Number ID in Meta Business Suite → WhatsApp → API Setup', 'Use a permanent System User Access Token from Meta Business Manager']} color="#5B4FE8" />
                <div>
                  <label style={lbl}>Phone Number ID <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={selfPhoneNumberId} onChange={e => setSelfPhoneNumberId(e.target.value)} placeholder="12345678901234" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="password" value={selfAccessToken} onChange={e => setSelfAccessToken(e.target.value)} placeholder="EAABkzp..." style={inp} />
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>Use a System User token — it won't expire</div>
                </div>
              </>)}

              {/* WhatsApp meta_connect — show connected number */}
              {botCategory === 'whatsapp' && whatsappMode === 'meta_connect' && (
                <div style={{ padding: '12px', background: '#10b98110', border: '1px solid #10b98130', borderRadius: '9px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                  <span style={{ color: '#6ee7b7', fontSize: '13px' }}>Connected: <strong>{waNumbers.find(n => n.id === selfPhoneNumberId)?.display_phone_number || selfPhoneNumberId}</strong></span>
                </div>
              )}

              {/* Facebook oauth — show connected page */}
              {botCategory === 'facebook' && facebookMode === 'oauth' && (
                <div style={{ padding: '12px', background: '#10b98110', border: '1px solid #10b98130', borderRadius: '9px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: '#10b981', fontSize: '18px' }}>✓</span>
                  <span style={{ color: '#6ee7b7', fontSize: '13px' }}>Page: <strong>{fbOAuthPages.find(p => p.id === fbPageId)?.name || fbPageId}</strong></span>
                </div>
              )}

              {/* Facebook we_setup */}
              {botCategory === 'facebook' && facebookMode === 'we_setup' && (<>
                <InfoBox items={['Our team connects your Page and configures the bot', 'Setup completes within 1–2 business days']} color="#10b981" />
                <div>
                  <label style={lbl}>Facebook Page Name or URL</label>
                  <input type="text" value={fbPageUrl} onChange={e => setFbPageUrl(e.target.value)} placeholder="facebook.com/yourbusiness" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={inp} />
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>Our team will contact you here</div>
                </div>
              </>)}

              {/* Facebook self_setup */}
              {botCategory === 'facebook' && facebookMode === 'self_setup' && (<>
                <InfoBox items={['Page ID in Facebook Page Settings → About', 'Page Access Token from Meta Developer Console (pages_messaging permission)']} color="#f59e0b" />
                <div>
                  <label style={lbl}>Facebook Page ID <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="text" value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="123456789012345" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Page Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="password" value={fbPageToken} onChange={e => setFbPageToken(e.target.value)} placeholder="EAABkzp..." style={inp} />
                </div>
                <div>
                  <label style={lbl}>Your Email</label>
                  <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={inp} />
                </div>
              </>)}

              {/* Website / Voice */}
              {(botCategory === 'website' || botCategory === 'voice') && (<>
                {botCategory === 'voice' && <InfoBox items={['Our team configures your voice bot', 'It learns from your website content', 'Setup takes 2–5 business days']} color="#f59e0b" />}
                <div>
                  <label style={lbl}>Website URL <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://yourbusiness.com" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                  <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={inp} />
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>We'll send your embed code here</div>
                </div>
              </>)}

              {/* AI Bot */}
              {isAIFlow && (<>
                <InfoBox items={['Your bot answers every message using AI', 'Pick a dataset below for it to answer from — change anytime in Settings', 'Update tone and system prompt any time']} color="#a855f7" />
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <label style={{ ...lbl, marginBottom: 0 }}>Knowledge Base Dataset <span style={{ color: 'var(--text-faint)', textTransform: 'none' as const, fontWeight: 400 }}>(optional)</span></label>
                    <button type="button" onClick={loadDatasets} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontSize: '11px' }}>↻ Refresh</button>
                  </div>
                  {datasetsLoading ? (
                    <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Loading datasets…</div>
                  ) : datasets.length === 0 ? (
                    <div style={{ background: 'var(--bg-element)', borderRadius: '8px', padding: '12px', fontSize: '12px', color: 'var(--text-muted)' }}>
                      No datasets yet.{' '}
                      <a href="/datasets" target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8' }}>Upload one</a>{' '}
                      then hit Refresh.
                    </div>
                  ) : (
                    <select value={selectedDatasetId} onChange={e => setSelectedDatasetId(e.target.value)} style={inp}>
                      <option value="">— Add later in Bot Settings —</option>
                      {datasets.map(d => <option key={d.id} value={d.id}>{d.name} ({d.format})</option>)}
                    </select>
                  )}
                </div>
                {aiChannel === 'whatsapp' && (<>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px' }}>WhatsApp Business API credentials</div>
                  <InfoBox items={['Phone Number ID in Meta Business Suite → WhatsApp → API Setup', 'Use a permanent System User Access Token']} color="#10b981" />
                  <div>
                    <label style={lbl}>Phone Number ID <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" value={selfPhoneNumberId} onChange={e => setSelfPhoneNumberId(e.target.value)} placeholder="12345678901234" style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="password" value={selfAccessToken} onChange={e => setSelfAccessToken(e.target.value)} placeholder="EAABkzp..." style={inp} />
                  </div>
                </>)}
                {aiChannel === 'facebook' && (<>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px' }}>Facebook Page credentials</div>
                  <InfoBox items={['Page ID in Facebook Page Settings → About', 'Page Access Token from Meta Developer Console (pages_messaging)']} color="#3b82f6" />
                  <div>
                    <label style={lbl}>Facebook Page ID <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="text" value={fbPageId} onChange={e => setFbPageId(e.target.value)} placeholder="123456789012345" style={inp} />
                  </div>
                  <div>
                    <label style={lbl}>Page Access Token <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="password" value={fbPageToken} onChange={e => setFbPageToken(e.target.value)} placeholder="EAABkzp..." style={inp} />
                  </div>
                </>)}
                {aiChannel === 'website' && (
                  <div>
                    <label style={lbl}>Your Email <span style={{ color: '#ef4444' }}>*</span></label>
                    <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} placeholder="you@business.com" style={inp} />
                    <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>We'll send your widget embed code here</div>
                  </div>
                )}
              </>)}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={() => goBack(3)} style={btnGhost}>← Back</button>
              <button onClick={() => isAIFlow ? createBot() : goNext(3)} disabled={!canProceedStep3 || loading} style={{ ...btnPrimary(!canProceedStep3 || loading), flex: 1 }}>
                {isAIFlow ? (loading ? 'Creating…' : '✨ Create AI Bot') : 'Continue →'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Template ── */}
        {step === 4 && (
          <div>
            <div style={{ color: 'var(--text-faint)', fontSize: '13px', marginBottom: '20px' }}>Pre-filled with smart replies for your industry. Edit everything after.</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              {industries.map(ind => {
                const t = INDUSTRY_TEMPLATES[ind.id];
                const sel = selectedTemplate === ind.id;
                return (
                  <div key={ind.id} onClick={() => setSelectedTemplate(ind.id)} style={{ padding: '16px 12px', border: sel ? '2px solid #5B4FE8' : '1px solid var(--card-border)', borderRadius: '13px', cursor: 'pointer', background: sel ? '#5B4FE810' : 'var(--bg-card)', textAlign: 'center', minHeight: '44px' }}>
                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>{t?.icon || '🤖'}</div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '12px', marginBottom: '3px' }}>{ind.name.replace(/^[^ ]+ /, '')}</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>{ind.description}</div>
                    {sel && <div style={{ color: '#5B4FE8', fontSize: '10px', fontWeight: '700', marginTop: '6px' }}>✓ Selected</div>}
                  </div>
                );
              })}
              {firebaseTemplates.map(t => {
                const sel = selectedTemplate === t.id;
                const isOwn = auth.currentUser && t.createdBy === auth.currentUser.uid;
                return (
                  <div key={t.id} onClick={() => setSelectedTemplate(t.id)} style={{ padding: '16px 12px', border: sel ? '2px solid #5B4FE8' : '1px solid var(--card-border)', borderRadius: '13px', cursor: 'pointer', background: sel ? '#5B4FE810' : 'var(--bg-card)', textAlign: 'center', position: 'relative' }}>
                    {isOwn && !t.isPublic && <div style={{ position: 'absolute', top: '6px', right: '6px', background: '#10b98115', color: '#10b981', fontSize: '8px', fontWeight: '700', padding: '2px 5px', borderRadius: '8px' }}>Mine</div>}
                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>{t.icon || '🤖'}</div>
                    <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '12px', marginBottom: '3px' }}>{t.name}</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>{t.description || `${t.triggers?.length || 0} triggers`}</div>
                    {sel && <div style={{ color: '#5B4FE8', fontSize: '10px', fontWeight: '700', marginTop: '6px' }}>✓ Selected</div>}
                  </div>
                );
              })}
            </div>

            <div style={{ background: '#5B4FE810', border: '1px solid #5B4FE830', borderRadius: '13px', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text-primary)', marginBottom: '10px' }}>✨ What happens next</div>
              {['Bot launches with pre-built conversation flows', 'Customise everything in the visual Flow Builder', 'Track conversations & performance in your dashboard'].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '5px' }}>
                  <span style={{ color: '#5B4FE8', fontSize: '11px', marginTop: '3px' }}>→</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{item}</span>
                </div>
              ))}
              {setupFee > 0 && (
                <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f59e0b0d', border: '1px solid #f59e0b30', borderRadius: '9px' }}>
                  <div style={{ color: '#fbbf24', fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>Invoice: ${setupFee}</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>You'll be taken to Billing to pay. Bot activates after payment.</div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => goBack(4)} style={btnGhost}>← Back</button>
              <button onClick={createBot} disabled={loading} style={{ ...btnPrimary(loading), flex: 1 }}>
                {loading ? 'Creating…' : setupFee > 0 ? `✨ Create & Invoice ($${setupFee})` : '✨ Create Bot'}
              </button>
            </div>
          </div>
        )}

      </div>
    </MobileLayout>
  );
}
